import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from "@tanstack/react-query"
import { carrotClient } from "#/utils/CarrotClient"
import ResponseError from "#/types/responseError"
import buildCarrotApiRetryPolicy from "#/utils/CarrotApiRetryPolicy"
import { CategoryEntry, getEntryWithMatchingCategory } from "#/components/read/shared/getEntryWithCategory"
import { BookmarkEntryWithMatchingCategory } from "#/components/read/shared/BookmarkMapper"
import useGetCategories from "#/components/read/shared/useGetCategories"

export interface MemberBookmarkedContentItem {
  memberBookmarkedContentId: number
  employeeId: number
  partnerAccessId?: number
  contentfulEntryId: string
  categoryEntryId: string
  contentType: string
  createdAtUtc: string
}
export interface AddBookmarkRequest {
  contentfulEntryId: string
  categoryEntryId: string
  contentType: string
}
export interface RemoveBookmarkRequest {
  contentfulEntryId: string
  categoryEntryId: string
}

const MEMBER_BOOKMARKED_CONTENT_QUERY_KEY = "memberBookmarkedContent"

export function useMemberBookmarkedContent(
  categories: CategoryEntry[]
): UseQueryResult<BookmarkEntryWithMatchingCategory[], ResponseError> {
  return useQuery<MemberBookmarkedContentItem[], ResponseError, BookmarkEntryWithMatchingCategory[]>(
    [MEMBER_BOOKMARKED_CONTENT_QUERY_KEY],
    () => carrotClient.getMemberBookmarkedContent(),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: buildCarrotApiRetryPolicy(3),
      staleTime: 60000,
      select: (bookmarks) =>
        bookmarks
          .sort((a, b) => new Date(b.createdAtUtc).getTime() - new Date(a.createdAtUtc).getTime())
          .filter(
            (bookmark, index, array) =>
              array.findIndex((item) => item.contentfulEntryId === bookmark.contentfulEntryId) === index
          )
          .map((bookmark) =>
            getEntryWithMatchingCategory(bookmark.contentfulEntryId, bookmark.categoryEntryId, categories)
          )
          .filter((entry): entry is BookmarkEntryWithMatchingCategory => !!entry)
    }
  )
}

export function useAddMemberBookmarkedContent(): UseMutationResult<void, ResponseError, AddBookmarkRequest> {
  const queryClient = useQueryClient()

  return useMutation<void, ResponseError, AddBookmarkRequest>(
    ({ contentfulEntryId, categoryEntryId, contentType }) =>
      carrotClient.addMemberBookmarkedContent(contentfulEntryId, categoryEntryId, contentType),
    {
      retry: buildCarrotApiRetryPolicy(1),
      onSuccess: () => {
        queryClient.invalidateQueries([MEMBER_BOOKMARKED_CONTENT_QUERY_KEY])
      }
    }
  )
}

export function useRemoveMemberBookmarkedContent(): UseMutationResult<void, ResponseError, RemoveBookmarkRequest> {
  const queryClient = useQueryClient()

  return useMutation<void, ResponseError, RemoveBookmarkRequest>(
    ({ contentfulEntryId, categoryEntryId }) =>
      carrotClient.removeMemberBookmarkedContent(contentfulEntryId, categoryEntryId),
    {
      retry: buildCarrotApiRetryPolicy(1),
      onSuccess: () => {
        queryClient.invalidateQueries([MEMBER_BOOKMARKED_CONTENT_QUERY_KEY])
      }
    }
  )
}

export function useIsMemberBookmarkedContent(
  contentfulEntryId: string,
  categoryEntryId: string
): {
  isFavorited: boolean
  isLoading: boolean
} {
  const { data: categories, isLoading: isCategoriesLoading } = useGetCategories()

  const { data: bookmarks, isLoading: isBookmarkedContentLoading } = useMemberBookmarkedContent(categories)

  const isFavorited =
    bookmarks?.some((item) => item.articleId === contentfulEntryId && item.category.id === categoryEntryId) ?? false

  return { isFavorited, isLoading: isCategoriesLoading || isBookmarkedContentLoading }
}
